/**
 * Куди вести користувача зі сповіщення (тип + payload з API).
 * Повертає об'єкт для react-router `navigate()` або null.
 */

function q(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function orderDest(orderId, extra = {}) {
  if (orderId != null) {
    return { pathname: '/planner-v2', search: q({ order: orderId, ...extra }) };
  }
  return { pathname: '/planner-v2' };
}

/**
 * Маршрутизація за action_type з activity_rule (правила сповіщень).
 */
function destinationFromActivityAction(actionType, p) {
  switch (actionType) {
    case 'stock_transfer':
    case 'stock_arrival':
    case 'stock_consumption':
    case 'stock_block':
      return { pathname: '/stock', search: q({ tab: 'arrivals' }) };
    case 'purchase_create':
    case 'purchase_approve':
    case 'purchase_receive':
      return {
        pathname: '/purchases',
        search: q(p.purchase_id || p.entity_id ? { highlight_po: p.purchase_id || p.entity_id } : {}),
      };
    case 'order_create':
    case 'order_update':
    case 'order_ship':
      return { pathname: '/customer-orders' };
    case 'production_create':
    case 'production_update':
    case 'setup_requested':
    case 'operator_shift_report':
      return orderDest(p.order_id || p.entity_id, p.report_ids ? { shiftReports: String(p.report_ids) } : {});
    case 'mixing_create':
      return { pathname: '/production-mixing' };
    case 'crushing_start':
    case 'crushing_complete':
      return { pathname: '/crushing' };
    case 'maintenance_create':
      return { pathname: '/maintenance-kanban' };
    case 'lead_create':
    case 'project_create':
    case 'project_update':
      return { pathname: '/rnd' };
    default:
      return null;
  }
}

export function getNotificationDestination(notification) {
  if (!notification || !notification.type) return null;
  const type = notification.type;
  const p =
    notification.payload && typeof notification.payload === 'object' ? notification.payload : {};

  switch (type) {
    case 'activity_rule':
      return destinationFromActivityAction(p.action_type, p);

    case 'purchase_request_created':
      return {
        pathname: '/purchases',
        search: q({
          view: 'requests',
          ...(p.purchase_request_id != null ? { highlight_pr: p.purchase_request_id } : {}),
        }),
      };

    case 'purchase_awaiting_approval':
      return {
        pathname: '/purchase-approvals',
        search: q(p.purchase_id != null ? { highlight_po: p.purchase_id } : {}),
      };

    case 'purchase_approval_decided':
    case 'purchase_completed':
    case 'purchase_status_changed':
      return {
        pathname: '/purchases',
        search: q(p.purchase_id != null ? { highlight_po: p.purchase_id } : {}),
      };

    case 'production_report_created':
    case 'production_status_changed':
    case 'setup_completed':
      return orderDest(p.order_id);

    case 'production_qty_reached':
    case 'machine_break_started':
    case 'machine_break_ended':
    case 'production_cancelled':
      if (p.nav === 'operator') {
        return { pathname: '/production-operator-v2' };
      }
      return {
        pathname: '/shift-floor',
        search: q(p.machine_id != null ? { machine: p.machine_id } : {}),
      };

    case 'deadline_risk':
      // Спочатку цех (відкриє картку ТПА з ?machine=); якщо немає machine — планер по ЗВ
      if (p.machine_id != null) {
        return {
          pathname: '/shift-floor',
          search: q({
            machine: p.machine_id,
            ...(p.order_id != null ? { order: p.order_id } : {}),
          }),
        };
      }
      if (p.order_id != null) {
        return { pathname: '/production', search: q({ order: p.order_id }) };
      }
      return { pathname: '/shift-floor' };

    case 'mixing_complete':
      if (p.nav === 'operator') {
        return { pathname: '/production-operator-v2' };
      }
      return { pathname: '/production-mixing' };

    case 'packaging_complete':
      return { pathname: '/packaging' };

    case 'stock_material_request_done':
    case 'stock_material_request_cancelled':
      return { pathname: '/stock', search: q({ tab: 'raw' }) };

    case 'customer_order_shipped':
      return {
        pathname: '/customer-orders',
        search: q(p.customer_order_id != null ? { highlight: p.customer_order_id } : {}),
      };

    case 'machine_break_planned':
      return { pathname: '/production-operator-v2' };

    case 'operator_shift_completed': {
      const ids = Array.isArray(p.report_ids) && p.report_ids.length
        ? p.report_ids.join(',')
        : Array.isArray(p.order_ids) && p.order_ids.length
          ? String(p.order_ids[0])
          : null;
      if (Array.isArray(p.report_ids) && p.report_ids.length) {
        return {
          pathname: '/planner-v2',
          search: q({ shiftReports: p.report_ids.join(',') }),
        };
      }
      if (Array.isArray(p.order_ids) && p.order_ids.length) {
        return orderDest(p.order_ids[0], { focus: 'shift-reports' });
      }
      return { pathname: '/planner-v2', search: q(ids ? { order: ids } : {}) };
    }

    case 'order_created':
      return { pathname: '/customer-orders' };

    case 'order_scheduled_mixing':
      return { pathname: '/production-mixing' };

    case 'drying_completed':
      return { pathname: '/production-drying' };

    case 'stock_finished_receipt':
    case 'stock_finished_shipment':
      return { pathname: '/stock', search: q({ tab: 'finished' }) };

    case 'stock_transaction':
    case 'stock_material_request':
    case 'pws_critical_low':
    case 'low_stock':
      return { pathname: '/stock', search: q({ tab: 'raw' }) };

    case 'stock_arrival_pending':
      return { pathname: '/stock', search: q({ tab: p.stock_tab || 'packaging' }) };

    case 'crushing_created':
    case 'crushing_confirmed':
      return { pathname: '/crushing' };

    case 'quality_assigned':
    case 'quality_passed':
    case 'quality_failed':
      // ВКЯ / ролі QC → канбан; персонально оператору — робоче місце
      if (p.nav === 'operator') {
        return { pathname: '/production-operator-v2' };
      }
      return {
        pathname: '/qc',
        search: q(
          p.order_quality_check_id
            ? { tab: 'requests', highlight: p.order_quality_check_id }
            : p.order_id
              ? { tab: 'requests', order: p.order_id }
              : { tab: 'requests' }
        ),
      };

    case 'operator_order_assigned':
    case 'operator_order_on_machine':
      return { pathname: '/production-operator-v2' };

    case 'operator_order_response':
      return orderDest(p.order_id);

    case 'maintenance_alert':
      return {
        pathname: '/maintenance-kanban',
        search: q(p.ticket_id != null ? { ticket: p.ticket_id } : {}),
      };

    case 'packaging_qc_cleared':
      return { pathname: '/packaging' };

    default:
      return null;
  }
}
